/**
 * 체감온도 (apparent temperature) as the legacy dashboard computed it: 기상청 여름철 공식 with the
 * Stull (2011) wet-bulb approximation. Ported unchanged from `_wxWetBulb` / `_wxFeelsLike` so that
 * new records stay comparable with the 844 migrated legacy hourly records, which were written with
 * exactly these coefficients.
 */
export function wetBulbC(airTemperatureC: number, relativeHumidityPct: number): number {
  const ta = airTemperatureC;
  const rh = relativeHumidityPct;
  return (
    ta * Math.atan(0.151977 * Math.pow(rh + 8.313659, 0.5)) +
    Math.atan(ta + rh) -
    Math.atan(rh - 1.676331) +
    0.00391838 * Math.pow(rh, 1.5) * Math.atan(0.023101 * rh) -
    4.686035
  );
}

export function apparentTemperatureC(
  airTemperatureC: number,
  relativeHumidityPct: number,
): number | null {
  if (!Number.isFinite(airTemperatureC) || !Number.isFinite(relativeHumidityPct)) return null;
  if (relativeHumidityPct < 0 || relativeHumidityPct > 100) return null;
  const tw = wetBulbC(airTemperatureC, relativeHumidityPct);
  const feels =
    -0.2442 +
    0.55399 * tw +
    0.45535 * airTemperatureC -
    0.0022 * tw * tw +
    0.00278 * tw * airTemperatureC +
    3.0;
  return Number.isFinite(feels) ? feels : null;
}

export type LegacyHeatStage = '정상' | '관심' | '주의' | '경고' | '위험';
export type LegacyHeatAdvisory = { stage: LegacyHeatStage; action: string };

/**
 * The legacy `_wxStage` table. This is advisory only: the official stage of a stored record still
 * comes from an approved policy snapshot through `classify`, which stays 미분류 until a policy is
 * approved. Nothing here promotes a record to a confirmed stage.
 */
export function legacyHeatAdvisory(feelsLikeC: number): LegacyHeatAdvisory {
  if (feelsLikeC >= 38) return { stage: '위험', action: '즉시 작업 중지 · 응급 조치' };
  if (feelsLikeC >= 35) return { stage: '경고', action: '작업 조정 · 중지 검토' };
  if (feelsLikeC >= 33) return { stage: '주의', action: '물 · 그늘 · 10분 휴식' };
  if (feelsLikeC >= 31) return { stage: '관심', action: '충분한 수분 섭취 · 그늘 휴식' };
  return { stage: '정상', action: '정상 근무' };
}
